import React, { useState, useEffect, useRef } from "react";
import {
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Helmet } from "react-helmet";
// Styles
import { useTheme } from "@mui/material/styles";
import useStyles from "assets/jss/material-react/views/italmundoStyles";
// Components
import {
  Grid,
  Box,
  Button,
  Checkbox,
  useMediaQuery,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Container,
  Typography,
} from "@mui/material";
import { Redireccion, ModalGenerico } from "../../components/Modals";
// Assets
import {
  IconoMensajeErrorItal,
  LogoItalmundo,
  ValidarDocumentosIcon,
  CheckModalIcon,
  DomicilioIcon,
  CargaDocumentosIcon,
  CheckDocumentosIcon,
} from "assets";
// Services
import PublicServices from "services/publicService";
import ClientService from "services/clientService";
import { getSessionDocument } from "services/sessionStorageService";
import { redirectStep, URLs } from "../../utils/redirectStep";
const baseUrlCanonical = window.baseUrlCanonical;
const limiteArchivo = window.limiteArchivo;

function SolicitarMiTarjeta() {
  //Inicializaciones base
  const clientService = new ClientService();
  const publicService = new PublicServices();
  const location = useLocation();
  const { tipoTarjeta } = useParams();
  const canonicalUrl = `${baseUrlCanonical}${URLs.solicitarMiTarjeta}/${tipoTarjeta}`;
  const classes = useStyles();
  const navigate = useNavigate();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = React.useState(false);

  const [modal, setModal] = React.useState({
    open: false,
    title: "",
    message: "",
    content: null,
  });

  // Inicializaciones particulares
  const [allSucursales, setAllSucursales] = React.useState([]);
  const [sucursales, setSucursales] = React.useState([]);
  const [localidades, setLocalidades] = React.useState([]);
  const [allLocalidades, setAllLocalidades] = React.useState([]);
  const [localidadId, setLocalidadId] = React.useState("");
  const [departamentos, setDepartamentos] = React.useState([]);
  const [departamentoId, setDepartamentoId] = React.useState("");
  const [terminosYCondiciones, setTerminosYCondiciones] = React.useState([]);
  const fileInputRefs = useRef({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [agenciaRetiro, setAgenciaRetiro] = useState({
    idAgenciaRetiro: "",
    documentacionRequerida: [],
  });
  const [terminos, setTerminos] = useState({
    idTermino: null,
    aceptado: false,
  });
  const [errors, setErrors] = useState({});
  const [documentStatesUpload, setDocumentStatesUpload] = useState({});

  useEffect(() => {
    const checkAndRedirect = async () => {
      const params = new URLSearchParams(searchParams);
      const documento = getSessionDocument();
      if (!documento) {
        navigate(`/?` + params);
      } else {
        try {
          const response = await clientService.evaluateDocument(documento);
          const codigoEstado = response.data.codigoEstado;
          const transaction = {
            ...response.data,
          };
          if (codigoEstado != 4) {
            openModalRedirect();
            setTimeout(async () => {
              await redirectStep(codigoEstado, transaction, navigate, params);
            }, 3000);
          }
        } catch (error) {
          console.log(error);
        }
      }
    };
    checkAndRedirect();
  }, []);

  const openModalRedirect = () => {
    setModal({
      open: true,
      showX: false,
      content: <Redireccion />,
    });
  };

  const buildNavigateUrl = (path) => {
    const params = new URLSearchParams(searchParams);
    return `${path}?${params.toString()}`;
  };

  const getSucursales = async () => {
    const getAllSucursales = await publicService.getAgenciasRedPagos();
    const formattedSucursales = getAllSucursales.data.map((sucursal) => ({
      ...sucursal,
    }));
    setAllSucursales(formattedSucursales);
    return formattedSucursales;
  };

  const getLocalidades = (sucursales) => {
    const localidades = sucursales
      .map((suc) => ({
        localidadBarrio: suc.localidadBarrio,
        idLocalidad: suc.idLocalidad,
        idDepto: suc.idDepto,
      }))
      .filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.idLocalidad === value.idLocalidad)
      )
      .sort((a, b) => a.localidadBarrio.localeCompare(b.localidadBarrio));
    setAllLocalidades(localidades);
  };

  const getDepartamentos = (sucursales) => {
    const departamentos = sucursales
      .map((suc) => ({
        departamento: suc.departamento,
        idDepto: suc.idDepto,
      }))
      .filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.idDepto === value.idDepto)
      )
      .sort((a, b) => a.departamento.localeCompare(b.departamento));
    setDepartamentos(departamentos);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const getTerminos = await publicService.getTerminosYCondiciones();
        setTerminosYCondiciones(getTerminos);

        const sucursales = await getSucursales();
        getLocalidades(sucursales);
        getDepartamentos(sucursales);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (
      location?.state?.transaction?.tipoDeEmpleoElegido?.documentacionRequerida
    ) {
      const docTypes =
        location?.state?.transaction?.tipoDeEmpleoElegido
          ?.documentacionRequerida;

      docTypes.forEach((doc) => {
        fileInputRefs.current[doc.descripcion] = React.createRef();
      });
    }
  }, [
    location?.state?.transaction?.tipoDeEmpleoElegido?.documentacionRequerida,
  ]);

  const handleModalCloseClick = () => setModal({ ...modal, open: false });

  const handleFileChange = (event, documento) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = ["image/png", "image/jpeg", "application/pdf"];
      const maxFileSize = parseInt(limiteArchivo) * 1024 * 1024;

      // Validar tipo de archivo
      if (!allowedTypes.includes(file.type)) {
        setModal({
          open: true,
          showX: true,
          message:
            "Formato de archivo no permitido. Sólo se aceptan PNG, JPG, JPEG o PDF.",
        });
        event.target.value = ""; // Limpiar input
        return;
      }

      // Validar tamaño del archivo
      if (file.size > maxFileSize) {
        setModal({
          open: true,
          showX: true,
          message: `El archivo no debe superar los ${limiteArchivo} MB.`,
        });
        event.target.value = ""; // Limpiar input
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(",")[1];
        const newDocument = {
          id: documento.id,
          base64: base64,
          type: file.type.split("/")[1] === "pdf" ? 2 : 1,
        };

        setAgenciaRetiro((prevState) => {
          const documentacionRequerida = prevState.documentacionRequerida;
          const existingIndex = documentacionRequerida.findIndex(
            (doc) => doc.id === newDocument.id
          );

          if (existingIndex !== -1) {
            documentacionRequerida[existingIndex] = newDocument;
          } else {
            documentacionRequerida.push(newDocument);
          }

          return {
            ...prevState,
            documentacionRequerida: [...documentacionRequerida],
          };
        });
        setDocumentStatesUpload((prevState) => ({
          ...prevState,
          [documento.id]: true,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOpenTermsModal = () =>
    setModal({
      open: true,
      showX: true,
      content: (
        <Box
          sx={{
            textAlign: "justify",
            maxHeight: "50vh",
            overflowY: "auto",
            padding: "20px",
            fontSize: "12px",
          }}
        >
          <p style={{ padding: "0rem 0.5rem" }}>
            {terminosYCondiciones.data.descripcion}
          </p>
        </Box>
      ),
    });

  const handleTerminosChange = (event) => {
    setTerminos({
      idTermino: terminosYCondiciones.data.id,
      aceptado: event.target.checked,
    });
  };

  const handleInputChange = async (e) => {
    const { name, value } = e.target;

    if (name === "idDepartamento") {
      const localidadesFiltradas = allLocalidades.filter(
        (x) => x.idDepto === value
      );
      setLocalidades(localidadesFiltradas);
      setDepartamentoId(value);
      setSucursales([]);
      setLocalidadId("");
      setAgenciaRetiro((prevState) => ({
        ...prevState,
        idAgenciaRetiro: "",
      }));
    }

    if (name === "idBarrio") {
      const sucursalesFiltradasPorBarrio = allSucursales.filter(
        (x) => x.idLocalidad === value
      );
      setAgenciaRetiro((prevState) => ({
        ...prevState,
        idAgenciaRetiro: "",
      }));
      setSucursales(sucursalesFiltradasPorBarrio);
      setLocalidadId(value);
    }

    if (name === "idAgenciaRetiro") {
      setAgenciaRetiro({ ...agenciaRetiro, [name]: value });
    }
  };

  const handleValidation = () => {
    let valid = true;
    let newErrors = {};

    if (!departamentoId) {
      newErrors.idDepartamento = "Este campo es obligatorio";
      valid = false;
    }

    if (!localidadId) {
      newErrors.idBarrio = "Este campo es obligatorio";
      valid = false;
    }

    for (const field in agenciaRetiro) {
      if (field === "idAgenciaRetiro" && !agenciaRetiro[field]) {
        newErrors[field] = "Este campo es obligatorio";
        valid = false;
      }
    }
    setErrors(newErrors);
    return valid;
  };

  const handleSolicitarMiTarjetaAhora = async () => {
    if (!handleValidation()) {
      return;
    }
    const cantidadDocumentosACargar =
      location.state.transaction.tipoDeEmpleoElegido.documentacionRequerida
        .length;
    const cantidadDocumentosCargados =
      agenciaRetiro.documentacionRequerida.length;
    if (cantidadDocumentosACargar > cantidadDocumentosCargados) {
      setModal({
        open: true,
        showX: true,
        img: IconoMensajeErrorItal,
        message:
          "Por favor, chequeá que todos los documentos hayan sido cargados correctamente.",
      });
      return;
    }
    if (!terminos.aceptado) {
      setModal({
        open: true,
        showX: true,
        message: "Debe aceptar los términos y condiciones para continuar.",
      });
      return;
    }
    setLoading(true);
    if (cantidadDocumentosACargar == cantidadDocumentosCargados) {
      try {
        await clientService.termsAndConditions(
          terminos,
          location.state.transaction
        );
      } catch (error) {
        setLoading(false);
        navigate(buildNavigateUrl(URLs.intenteLuego));
        return;
      }
      try {
        const responseAgency = await clientService.documentsAndBranchAgency(
          agenciaRetiro,
          location.state.transaction
        );
        const newTransaction = {
          ...location.state.transaction,
        };
        if (responseAgency.code == 200) {
          const responseRedirect = await clientService.redirectToAnexoServicios(
            location.state.transaction
          );
          if (responseRedirect.data.continueAnexoServicios) {
            navigate(buildNavigateUrl(URLs.adicionales), {
              state: {
                transaction: newTransaction,
              },
            });
          } else {
            navigate(buildNavigateUrl(URLs.verificacionCelular), {
              state: { transaction: newTransaction },
            });
          }
        } else {
          navigate(buildNavigateUrl(URLs.intenteLuego), {
            state: { transaction: newTransaction },
          });
        }
      } catch (error) {
        navigate(buildNavigateUrl(URLs.intenteLuego));
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  return (
    <>
      <Container
        maxWidth="md"
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "start",
          alignItems: "center",
          padding: "0",
          paddingBottom: "1rem",
        }}
      >
        <Helmet>
          <link rel="canonical" href={canonicalUrl} />
        </Helmet>
        <div className={classes.contentImageMobile}>
          <img
            src={LogoItalmundo}
            alt="Home Mobile"
            style={{
              width: "50%",
              maxWidth: "250px",
              maxHeight: "100%",
              objectFit: "contain",
              paddingTop: "0.5rem",
              margin: mobile ? "1rem" : "3rem",
            }}
          />
        </div>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "start",
              marginTop: "1rem",
              width: mobile ? "90%" : "100%",
            }}
          >
            <img
              src={ValidarDocumentosIcon}
              style={{
                marginRight: "8px",
                width: "24px",
                height: "24px",
              }}
            />
            <p
              style={{
                fontSize: mobile ? "12px" : "15px",
                fontWeight: "520",
              }}
            >
              CARGAR DOCUMENTOS
            </p>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "start",
              marginTop: mobile ? "0.5rem" : "1rem",
              width: mobile ? "90%" : "100%",
            }}
          >
            <p
              style={{
                fontSize: mobile ? "12px" : "14px",
              }}
            >
              Te solicitamos que todos los documentos que cargues sean legibles,
              con buena luz y que no se vean borrosos.
            </p>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              marginY: mobile ? "1rem" : "1.5rem",
              width: mobile ? "90%" : "100%",
            }}
          >
            <Grid container spacing={mobile ? 0.3 : 1}>
              {location?.state?.transaction?.tipoDeEmpleoElegido
                ?.documentacionRequerida &&
                location.state.transaction.tipoDeEmpleoElegido.documentacionRequerida.map(
                  (documento, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      key={index}
                      style={{ paddingLeft: mobile ? "18%" : "" }}
                    >
                      <input
                        id={`file-input-${index}`}
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        style={{ display: "none" }}
                        onChange={(e) => handleFileChange(e, documento)}
                        ref={fileInputRefs.current[documento.descripcion]}
                      />
                      <Box
                        onClick={() =>
                          fileInputRefs.current[
                            documento.descripcion
                          ].current.click()
                        }
                        style={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                      >
                        <img
                          src={
                            documentStatesUpload[documento.id]
                              ? CheckDocumentosIcon
                              : CargaDocumentosIcon
                          }
                          alt="Upload icon"
                          style={{
                            marginRight: "8px",
                            width: "45px",
                            height: "45px",
                            cursor: "pointer",
                          }}
                        />
                        <Typography
                          variant="body1"
                          style={{
                            fontSize: mobile ? "12px" : "14px",
                            cursor: "pointer",
                          }}
                        >
                          {documento.descripcion}
                        </Typography>
                      </Box>
                    </Grid>
                  )
                )}
            </Grid>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "start",
              marginTop: "1rem",
              width: mobile ? "90%" : "100%",
            }}
          >
            <img
              src={DomicilioIcon}
              style={{
                marginRight: "8px",
                width: "24px",
                height: "24px",
              }}
            />
            <p
              style={{
                fontSize: mobile ? "12px" : "15px",
                fontWeight: "520",
              }}
            >
              ¿DÓNDE QUERÉS RETIRAR TU TARJETA?
            </p>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "start",
              marginTop: mobile ? "0.5rem" : "1rem",
              width: mobile ? "90%" : "100%",
            }}
          >
            <p
              style={{
                fontSize: mobile ? "12px" : "14px",
              }}
            >
              Retirá tu tarjeta en la sucursal de RedPagos más cercana a tu
              domicilio.
            </p>
          </Box>
          <Grid container spacing={mobile ? 0 : 3.5}>
            <Grid item xs={12} sm={6} display="flex">
              <FormControl
                fullWidth
                variant="filled"
                error={!!errors.idDepartamento}
                helpertext={errors.idDepartamento}
                className={classes.formControlCustom}
                sx={{
                  margin: "auto",
                  marginTop: "1rem",
                  width: mobile ? "90%" : "100%",
                }}
              >
                <InputLabel id="departamento-label">Departamento*</InputLabel>
                <Select
                  labelId="departamento-label"
                  id="idDepartamento"
                  name="idDepartamento"
                  onChange={handleInputChange}
                  label="Departamento*"
                  value={departamentoId}
                  className={classes.inputCustom}
                  sx={{
                    "& .MuiMenuItem-root": {
                      boxShadow: "none",
                    },
                    "& .MuiPaper-root": {
                      border: "1px solid #d3d4d5",
                    },
                    "&:after": {
                      borderBottom: "2px solid rgb(4 128 143)",
                    },
                  }}
                >
                  {departamentos.map((depto) => (
                    <MenuItem key={depto.idDepto} value={depto.idDepto}>
                      {depto.departamento}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} display="flex">
              <FormControl
                fullWidth
                variant="filled"
                className={classes.formControlCustom}
                error={!!errors.idBarrio}
                sx={{
                  margin: "auto",
                  marginTop: "1rem",
                  width: mobile ? "90%" : "100%",
                }}
              >
                <InputLabel id="barrio-label">Barrio/Localidad*</InputLabel>
                <Select
                  labelId="barrio-label"
                  id="idBarrio"
                  name="idBarrio"
                  onChange={handleInputChange}
                  label="Barrio*"
                  className={classes.inputCustom}
                  value={localidadId}
                  sx={{
                    "& .MuiMenuItem-root": {
                      boxShadow: "none",
                    },
                    "& .MuiPaper-root": {
                      border: "1px solid #d3d4d5",
                    },
                    "&:after": {
                      borderBottom: "2px solid rgb(4 128 143)",
                    },
                  }}
                >
                  {departamentoId ? (
                    localidades.map((localidad) => (
                      <MenuItem
                        key={localidad.idLocalidad}
                        value={localidad.idLocalidad}
                      >
                        {localidad.localidadBarrio}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem key={0} value={0}>
                      Seleccione un departamento
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} display="flex">
              <FormControl
                fullWidth
                variant="filled"
                className={classes.formControlCustom}
                error={!!errors.idAgenciaRetiro}
                sx={{
                  margin: "auto",
                  marginTop: mobile ? "1rem" : "",
                  width: mobile ? "90%" : "100%",
                }}
              >
                <InputLabel id="sucursal-label">Sucursal*</InputLabel>
                <Select
                  labelId="sucursal-label"
                  id="idAgenciaRetiro"
                  name="idAgenciaRetiro"
                  className={classes.inputCustom}
                  value={agenciaRetiro.idAgenciaRetiro}
                  onChange={handleInputChange}
                  label="Sucursal*"
                  sx={{
                    "& .MuiMenuItem-root": {
                      boxShadow: "none",
                    },
                    "& .MuiPaper-root": {
                      border: "1px solid #d3d4d5",
                    },
                    "&:after": {
                      borderBottom: "2px solid rgb(4 128 143)",
                    },
                  }}
                >
                  {sucursales.length > 0 ? (
                    sucursales.map((sucursal) => (
                      <MenuItem
                        key={sucursal.idAgenciaRedPagos}
                        value={sucursal.idAgenciaRedPagos}
                      >
                        <p>
                          {sucursal.nombreAgenciaRedPagos}
                          <br />
                          <small> {sucursal.direccionAgenciaRedPagos}</small>
                        </p>
                      </MenuItem>
                    ))
                  ) : localidadId !== "" ? (
                    <MenuItem key={0} value={0}>
                      No hay sucursales
                    </MenuItem>
                  ) : (
                    <MenuItem key={0} value={0}>
                      Seleccione departamento y localidad
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: mobile ? "90%" : "100%",
            }}
          >
            <p
              style={{
                fontSize: "11px",
                lineHeight: "1.2",
                width: "100%",
                textAlign: "left",
                marginTop: "1rem",
              }}
            >
              *Todos los campos son obligatorios.
            </p>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "1rem",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  maxWidth: "380px",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={terminos.aceptado}
                      onChange={handleTerminosChange}
                      color="primary"
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      style={{
                        fontSize: mobile ? "10px" : "12px",
                        lineHeight: "1.2",
                        width: "90%",
                        marginBottom: "0.5rem",
                        margin: "auto",
                      }}
                    >
                      Al continuar acepta los{" "}
                      <span
                        onClick={handleOpenTermsModal}
                        style={{
                          cursor: "pointer",
                          fontWeight: "400",
                          color: "#04808f",
                        }}
                      >
                        Términos y condiciones
                      </span>
                    </Typography>
                  }
                />
                <div className={classes.divBtn}>
                  <Button
                    className={`${classes.btnFull} ${classes.btnAzul}`}
                    variant="text"
                    disabled={loading}
                    onClick={handleSolicitarMiTarjetaAhora}
                    sx={{
                      width: "100%",
                      backgroundColor: "#FFFF",
                    }}
                  >
                    {"SOLICITAR MI TARJETA"}
                  </Button>
                </div>
              </Box>
            </Box>
          </Box>
          {/* <p
            style={{
              fontSize: "11px",
              lineHeight: "1.2",
              width: "100%",
              textAlign: "left",
              marginTop: "1rem",
            }}
          >
            Todos los cargos incluidos en el estado de cuenta son obligatorios,
            a excepción del envío extraordinario y el cargo por envío del estado
            de cuenta (Circular 2016 BCU).
          </p> */}
        </Box>
      </Container>
      {loading && (
        <Box className={classes.overlay}>
          <CircularProgress sx={{ color: "#C4C4C4" }} />
        </Box>
      )}
      <ModalGenerico
        modal={modal}
        handleModalCloseClick={() => handleModalCloseClick()}
      />
    </>
  );
}

export default SolicitarMiTarjeta;
